/**
 * Asset registry admin page (PRD §5.7 A-01..A-04).
 *
 * Four collapsible sections — sites, corridors, segments, devices — each
 * with a read-only table and an inline "add" form gated to ADMIN role.
 *
 * Uses existing /api/v1/assets endpoints. No new API work required.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AdminNav } from "@/components/AdminNav";

interface Site {
  id: string;
  orgId: string;
  siteId: string;
  name: string;
  timezone: string;
  centroidLat: number | null;
  centroidLng: number | null;
}

interface Corridor {
  id: string;
  siteId: string;
  corridorId: string;
  name: string;
  assetType: string;
}

interface Segment {
  id: string;
  corridorId: string;
  siteId: string;
  segmentId: string;
  name: string | null;
  startChainage: number;
  endChainage: number;
}

interface Device {
  id: string;
  siteId: string;
  interrogatorId: string;
  vendor: string;
  model: string | null;
  state: string;
  lastPingAt: string | null;
}

export function AssetsAdminPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "ADMIN";

  return (
    <div className="space-y-5" data-testid="assets-admin-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <AdminNav />
        <span className="text-xs text-slate-500">
          {isAdmin ? "ADMIN — you can create" : "Read-only (requires ADMIN role)"}
        </span>
      </div>

      <SitesSection isAdmin={isAdmin} />
      <CorridorsSection isAdmin={isAdmin} />
      <SegmentsSection isAdmin={isAdmin} />
      <DevicesSection isAdmin={isAdmin} />
    </div>
  );
}

// ── Sites ────────────────────────────────────────────────────────────
function SitesSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ sites: Site[] }>({
    queryKey: ["admin", "sites"],
    queryFn: () => api<{ sites: Site[] }>("/assets/sites"),
  });
  const [form, setForm] = useState({
    org_id: "",
    site_id: "",
    name: "",
    timezone: "Asia/Kuala_Lumpur",
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api<{ site: Site }>("/assets/sites", {
        method: "POST",
        body: JSON.stringify(form),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "sites"] });
      setForm({ org_id: "", site_id: "", name: "", timezone: "Asia/Kuala_Lumpur" });
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "create site failed"),
  });

  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      data-testid="admin-sites-section"
    >
      <h2 className="text-sm font-semibold text-slate-200 mb-3">Sites</h2>
      <AssetTable
        columns={["site_id", "name", "timezone", "centroid"]}
        rows={(data?.sites ?? []).map((s) => ({
          site_id: s.siteId,
          name: s.name,
          timezone: s.timezone,
          centroid: s.centroidLat && s.centroidLng
            ? `${s.centroidLat.toFixed(3)}, ${s.centroidLng.toFixed(3)}`
            : "—",
        }))}
        empty={isLoading ? "Loading…" : "No sites yet."}
        testId="admin-sites-table"
      />
      {isAdmin && (
        <CreateForm
          testId="admin-sites-create"
          error={error}
          submitting={createMut.isPending}
          onSubmit={() => createMut.mutate()}
          fields={[
            {
              label: "Organisation UUID",
              value: form.org_id,
              onChange: (v) => setForm({ ...form, org_id: v }),
              placeholder: "org uuid (see `SELECT id FROM organisations`)",
            },
            {
              label: "Site ID (code)",
              value: form.site_id,
              onChange: (v) => setForm({ ...form, site_id: v }),
              placeholder: "SITE_SOUTH_PIPELINE",
            },
            {
              label: "Name",
              value: form.name,
              onChange: (v) => setForm({ ...form, name: v }),
              placeholder: "PETRONAS South Trunk Pipeline",
            },
          ]}
        />
      )}
    </section>
  );
}

// ── Corridors ────────────────────────────────────────────────────────
function CorridorsSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { data: sitesData } = useQuery<{ sites: Site[] }>({
    queryKey: ["admin", "sites"],
    queryFn: () => api<{ sites: Site[] }>("/assets/sites"),
  });
  const firstSiteId = sitesData?.sites[0]?.id ?? "";
  const [siteUuid, setSiteUuid] = useState(firstSiteId);
  const effectiveSiteUuid = siteUuid || firstSiteId;

  const { data: corridorsData, isLoading } = useQuery<{ corridors: Corridor[] }>({
    queryKey: ["admin", "corridors", effectiveSiteUuid],
    queryFn: () =>
      api<{ corridors: Corridor[] }>(
        `/assets/sites/${effectiveSiteUuid}/corridors`,
      ),
    enabled: Boolean(effectiveSiteUuid),
  });
  const [form, setForm] = useState({
    corridor_id: "",
    name: "",
    asset_type: "PIPELINE",
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api<{ corridor: Corridor }>("/assets/corridors", {
        method: "POST",
        body: JSON.stringify({ ...form, site_uuid: effectiveSiteUuid }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "corridors"] });
      setForm({ corridor_id: "", name: "", asset_type: "PIPELINE" });
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "create corridor failed"),
  });

  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      data-testid="admin-corridors-section"
    >
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-200">Corridors</h2>
        <select
          value={effectiveSiteUuid}
          onChange={(e) => setSiteUuid(e.target.value)}
          data-testid="admin-corridors-site-filter"
          className="bg-slate-950 border border-slate-700 rounded text-xs px-2 py-1"
        >
          {(sitesData?.sites ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.siteId}
            </option>
          ))}
        </select>
      </div>
      <AssetTable
        columns={["corridor_id", "name", "asset_type"]}
        rows={(corridorsData?.corridors ?? []).map((c) => ({
          corridor_id: c.corridorId,
          name: c.name,
          asset_type: c.assetType,
        }))}
        empty={isLoading ? "Loading…" : "No corridors for this site."}
        testId="admin-corridors-table"
      />
      {isAdmin && effectiveSiteUuid && (
        <CreateForm
          testId="admin-corridors-create"
          error={error}
          submitting={createMut.isPending}
          onSubmit={() => createMut.mutate()}
          fields={[
            {
              label: "Corridor ID",
              value: form.corridor_id,
              onChange: (v) => setForm({ ...form, corridor_id: v }),
              placeholder: "CORRIDOR_SOUTH_TRUNK",
            },
            {
              label: "Name",
              value: form.name,
              onChange: (v) => setForm({ ...form, name: v }),
              placeholder: "South Trunk Pipeline",
            },
            {
              label: "Asset type",
              value: form.asset_type,
              onChange: (v) => setForm({ ...form, asset_type: v }),
              placeholder: "PIPELINE | RAIL | CABLE",
            },
          ]}
        />
      )}
    </section>
  );
}

// ── Segments ─────────────────────────────────────────────────────────
function SegmentsSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { data: sitesData } = useQuery<{ sites: Site[] }>({
    queryKey: ["admin", "sites"],
    queryFn: () => api<{ sites: Site[] }>("/assets/sites"),
  });
  const firstSiteId = sitesData?.sites[0]?.id ?? "";
  const { data: corridorsData } = useQuery<{ corridors: Corridor[] }>({
    queryKey: ["admin", "corridors", firstSiteId],
    queryFn: () =>
      api<{ corridors: Corridor[] }>(`/assets/sites/${firstSiteId}/corridors`),
    enabled: Boolean(firstSiteId),
  });

  const [corridorUuid, setCorridorUuid] = useState("");
  const effectiveCorridorUuid =
    corridorUuid || corridorsData?.corridors[0]?.id || "";

  const { data: segmentsData, isLoading } = useQuery<{ segments: Segment[] }>({
    queryKey: ["admin", "segments", effectiveCorridorUuid],
    queryFn: () =>
      api<{ segments: Segment[] }>(
        `/assets/corridors/${effectiveCorridorUuid}/segments`,
      ),
    enabled: Boolean(effectiveCorridorUuid),
  });

  const [form, setForm] = useState({
    segment_id: "",
    start_chainage: 0,
    end_chainage: 1,
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => {
      const corridor = (corridorsData?.corridors ?? []).find(
        (c) => c.id === effectiveCorridorUuid,
      );
      if (!corridor) throw new ApiError(400, "select a corridor first");
      return api<{ segment: Segment }>("/assets/segments", {
        method: "POST",
        body: JSON.stringify({
          corridor_uuid: effectiveCorridorUuid,
          site_uuid: corridor.siteId,
          ...form,
        }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "segments"] });
      setForm({ segment_id: "", start_chainage: 0, end_chainage: 1 });
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "create segment failed"),
  });

  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      data-testid="admin-segments-section"
    >
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-200">Segments</h2>
        <select
          value={effectiveCorridorUuid}
          onChange={(e) => setCorridorUuid(e.target.value)}
          data-testid="admin-segments-corridor-filter"
          className="bg-slate-950 border border-slate-700 rounded text-xs px-2 py-1"
        >
          {(corridorsData?.corridors ?? []).map((c) => (
            <option key={c.id} value={c.id}>
              {c.corridorId}
            </option>
          ))}
        </select>
      </div>
      <AssetTable
        columns={["segment_id", "chainage", "name"]}
        rows={(segmentsData?.segments ?? []).map((s) => ({
          segment_id: s.segmentId,
          chainage: `${s.startChainage} → ${s.endChainage} km`,
          name: s.name ?? "—",
        }))}
        empty={isLoading ? "Loading…" : "No segments for this corridor."}
        testId="admin-segments-table"
      />
      {isAdmin && effectiveCorridorUuid && (
        <CreateForm
          testId="admin-segments-create"
          error={error}
          submitting={createMut.isPending}
          onSubmit={() => createMut.mutate()}
          fields={[
            {
              label: "Segment ID",
              value: form.segment_id,
              onChange: (v) => setForm({ ...form, segment_id: v }),
              placeholder: "KP-200",
            },
            {
              label: "Start chainage (km)",
              value: String(form.start_chainage),
              onChange: (v) =>
                setForm({ ...form, start_chainage: Number.parseFloat(v) || 0 }),
              placeholder: "200.0",
              type: "number",
            },
            {
              label: "End chainage (km)",
              value: String(form.end_chainage),
              onChange: (v) =>
                setForm({ ...form, end_chainage: Number.parseFloat(v) || 0 }),
              placeholder: "201.0",
              type: "number",
            },
          ]}
        />
      )}
    </section>
  );
}

// ── Devices ──────────────────────────────────────────────────────────
function DevicesSection({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient();
  const { data: sitesData } = useQuery<{ sites: Site[] }>({
    queryKey: ["admin", "sites"],
    queryFn: () => api<{ sites: Site[] }>("/assets/sites"),
  });
  const firstSiteId = sitesData?.sites[0]?.id ?? "";
  const [siteUuid, setSiteUuid] = useState(firstSiteId);
  const effectiveSiteUuid = siteUuid || firstSiteId;

  const { data: devicesData, isLoading } = useQuery<{ devices: Device[] }>({
    queryKey: ["admin", "devices", effectiveSiteUuid],
    queryFn: () =>
      api<{ devices: Device[] }>(`/assets/sites/${effectiveSiteUuid}/devices`),
    enabled: Boolean(effectiveSiteUuid),
  });
  const [form, setForm] = useState({
    interrogator_id: "",
    vendor: "",
    model: "",
  });
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      api<{ device: Device }>("/assets/devices", {
        method: "POST",
        body: JSON.stringify({ site_uuid: effectiveSiteUuid, ...form }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "devices"] });
      setForm({ interrogator_id: "", vendor: "", model: "" });
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : "create device failed"),
  });

  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      data-testid="admin-devices-section"
    >
      <div className="flex items-center gap-3 mb-3">
        <h2 className="text-sm font-semibold text-slate-200">Interrogator devices</h2>
        <select
          value={effectiveSiteUuid}
          onChange={(e) => setSiteUuid(e.target.value)}
          data-testid="admin-devices-site-filter"
          className="bg-slate-950 border border-slate-700 rounded text-xs px-2 py-1"
        >
          {(sitesData?.sites ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.siteId}
            </option>
          ))}
        </select>
      </div>
      <AssetTable
        columns={["interrogator_id", "vendor", "model", "state", "last_ping"]}
        rows={(devicesData?.devices ?? []).map((d) => ({
          interrogator_id: d.interrogatorId,
          vendor: d.vendor,
          model: d.model ?? "—",
          state: d.state,
          last_ping: d.lastPingAt
            ? new Date(d.lastPingAt).toLocaleString()
            : "never",
        }))}
        empty={isLoading ? "Loading…" : "No devices for this site."}
        testId="admin-devices-table"
      />
      {isAdmin && effectiveSiteUuid && (
        <CreateForm
          testId="admin-devices-create"
          error={error}
          submitting={createMut.isPending}
          onSubmit={() => createMut.mutate()}
          fields={[
            {
              label: "Interrogator ID",
              value: form.interrogator_id,
              onChange: (v) => setForm({ ...form, interrogator_id: v }),
              placeholder: "INT-SOUTH-01",
            },
            {
              label: "Vendor",
              value: form.vendor,
              onChange: (v) => setForm({ ...form, vendor: v }),
              placeholder: "VenturaUK",
            },
            {
              label: "Model (optional)",
              value: form.model,
              onChange: (v) => setForm({ ...form, model: v }),
              placeholder: "DAS-Pro-Mk2",
            },
          ]}
        />
      )}
    </section>
  );
}

// ── Shared primitives ────────────────────────────────────────────────
function AssetTable({
  columns,
  rows,
  empty,
  testId,
}: {
  columns: string[];
  rows: Record<string, string>[];
  empty: string;
  testId: string;
}) {
  if (rows.length === 0) {
    return (
      <div className="text-xs text-slate-500 italic py-2" data-testid={`${testId}-empty`}>
        {empty}
      </div>
    );
  }
  return (
    <table className="w-full text-xs" data-testid={testId}>
      <thead>
        <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
          {columns.map((c) => (
            <th key={c} className="py-1 font-medium">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-slate-900">
            {columns.map((c) => (
              <td key={c} className="py-1.5 font-mono text-slate-300">
                {row[c]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface CreateFormField {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}

function CreateForm({
  testId,
  fields,
  onSubmit,
  submitting,
  error,
}: {
  testId: string;
  fields: CreateFormField[];
  onSubmit: () => void;
  submitting: boolean;
  error: string | null;
}) {
  return (
    <form
      className="mt-3 pt-3 border-t border-slate-800 space-y-2"
      data-testid={testId}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {fields.map((f) => (
          <label key={f.label} className="block">
            <span className="text-[11px] text-slate-500 uppercase tracking-wider">
              {f.label}
            </span>
            <input
              type={f.type ?? "text"}
              value={f.value}
              onChange={(e) => f.onChange(e.target.value)}
              placeholder={f.placeholder}
              className="mt-0.5 w-full bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs font-mono"
            />
          </label>
        ))}
      </div>
      {error && (
        <div
          className="text-xs text-rose-400"
          data-testid={`${testId}-error`}
        >
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        data-testid={`${testId}-submit`}
        className="text-xs inline-flex items-center gap-1 bg-teal-400 hover:bg-teal-300 text-slate-900 font-semibold px-3 py-1.5 rounded disabled:opacity-50"
      >
        <Plus className="size-3" />
        {submitting ? "Adding…" : "Add"}
      </button>
    </form>
  );
}
